import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Form, FormCheck, Modal } from "react-bootstrap";
import {
  handleAlert,
  snakeToText,
  validateEmails,
} from "@utils/helperFunctions";
import "./style/style.css";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import loadingGif from "../../../../assets/images/SS-icon-checkmark-cropped.gif";
import successVector from "./images/SS-success-vector.png";
import successCircle from "./images/success-circle.png";
import errorVector from "./images/SS-error-vector.png";
import errorCircle from "./images/error-circle.png";
import StationaryModal from "@components/StationaryModal/StationaryModal";
import FolderTreeComponentModal from "@components/FolderTreeComponent/FolderTreeComponentModal";
import { MultiSelectAutocomplete } from "@components/FormsV2/MultiSelectAutoComplete";
import { withErrorReason } from "@components/CatalogIndex/saveErrorToast";
import {
  generatedV2,
  useV2CreateDocumentRequestMutation,
  useV2GetAccountQuery,
  useV2GetDocumentRequestQuery,
  useV2ListAccountConnectionsQuery,
  useV2ListConnectedAppQuery,
  useV2ListProjectDownloadTypesForProjectQuery,
  V2DocumentRequestResource,
  V2ProjectDocumentResource,
  V2ProjectDownloadTypeResource,
  V2ProjectResource,
} from "@generated";
import type { RootState } from "@reducers/rootReducer";
import type { AppDispatch } from "../../store";
import useDownloadGeneratedDocument from "./api/useDownloadGeneratedDocument";

interface ProjectDownloadType {
  value: string;
  label: string;
  filename: string;
  type: string;
}

interface GenerateDocumentOption {
  name: string;
  id: string;
}

type TemplateDocumentType = NonNullable<
  V2DocumentRequestResource["attributes"]["templates"]
>[number]["document_type"];

const DOCUMENT_REQUEST_POLL_MS = 5000;

const toDocumentType = (
  dt: V2ProjectDownloadTypeResource
): ProjectDownloadType => {
  const { name, filename, "document-type": documentType } = dt.attributes;
  const templateId = dt.attributes["template-id"];
  return {
    value: templateId as string,
    label: name as string,
    filename: filename as string,
    type: documentType as string,
  };
};

/**
 * Refetches every cached v2 project query for this project (whatever include
 * list the caller used), so a parent holding `useV2GetProjectByIdQuery` sees
 * the storage-options the delivery just wrote. Fires nothing when no one has
 * that project cached.
 */
const refreshCachedProject =
  (projectId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const queries =
      (getState() as any)?.[generatedV2.reducerPath]?.queries ?? {};
    Object.values(queries).forEach((entry: any) => {
      if (
        entry?.endpointName === "v2GetProjectById" &&
        String(entry?.originalArgs?.id) === String(projectId)
      ) {
        dispatch(
          generatedV2.endpoints.v2GetProjectById.initiate(entry.originalArgs, {
            subscribe: false,
            forceRefetch: true,
          })
        );
      }
    });
  };

interface Props {
  orientation: "horizontal" | "vertical" | string;
  show: boolean;
  setShow: (show: boolean) => void;
  /** Unused since the v2 migration (v2 is token-scoped); still accepted. */
  account_slug?: string;
  project_id: string | number;
  saveToCloudStorageFlag: boolean;
  isStationary: boolean;
  authorizationCode: string;
  projectPassed?: V2ProjectResource;
  /**
   * Reports a failed download to the caller instead of this component's own
   * toast. A caller that unmounts this component on close passes it so the
   * error still surfaces if the modal was closed mid-download.
   */
  onDownloadError?: (message: string) => void;
}

const GenerateDocument = ({
  orientation,
  show,
  setShow,
  project_id,
  saveToCloudStorageFlag,
  isStationary,
  authorizationCode,
  projectPassed,
  onDownloadError,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const projectId = String(project_id);
  const project = projectPassed;

  const [selectedDocumentType, setSelectedDocumentType] =
    useState<ProjectDownloadType>({
      label: "",
      value: "",
      filename: "",
      type: "",
    });
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<
    ProjectDownloadType[]
  >([]);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState("download");
  const [emailFieldChecked, setEmailFieldChecked] = useState(false);
  const [saveToCloudStorageChecked, setSaveToCloudStorageChecked] =
    useState(false);
  const [emailList, setEmailList] = useState("");
  const [emailsAreInvalid, setEmailsAreInvalid] = useState(false);
  const [emailListChanged, setEmailListChanged] = useState(false);
  // From v2/document-requests
  const [documentRequestId, setDocumentRequestId] = useState<string | null>(
    null
  );
  const [documentRequestSettled, setDocumentRequestSettled] = useState(false);
  const [documentURL, setDocumentURL] = useState<string | null>(null);
  const [errorMessages, setErrorMessages] = useState<string | JSX.Element>("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessfulGenerationModal, setshowSuccessfulGenerationModal] =
    useState(false);
  // The parent may mount this component only while `show` is true, so the
  // form modal is hidden locally while a generation is in flight; `setShow`
  // is only called once the user is done with the result modal. Otherwise
  // closing the form would unmount the result modal and stop the polling.
  const [generationInFlight, setGenerationInFlight] = useState(false);
  const [showFileTreeModal, setShowFileTreeModal] = useState(false);
  const [storageIntegrations, setStorageIntegrations] = useState<any[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<any>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [generatePdf, setGeneratePdf] = useState(false);
  const [forceRegeneration, setForceRegeneration] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState({ id: "0", name: "" });
  const [folderLoading, setFolderLoading] = useState(true);
  const [generationStatus, setGenerationStatus] = useState<
    "loading" | "success" | "error"
  >("loading");
  const [step, setStep] = useState<number>(1);
  const [downloadError, setDownloadError] = useState("");

  const storageOptions = project?.attributes?.["storage-options"];

  // Generated documents are stored privately and served through an
  // authenticated API proxy, so the download has to carry the bearer token.
  const downloadGeneratedDocument =
    useDownloadGeneratedDocument(authorizationCode);

  const handleDownload = async (url: string, fallbackFilename?: string) => {
    try {
      setDownloadError("");
      await downloadGeneratedDocument(url, fallbackFilename);
    } catch {
      const message = "Could not download the document. Please try again.";
      if (onDownloadError) onDownloadError(message);
      else setDownloadError(message);
    }
  };

  // E-signature check. The API keeps the predicate's `?` on the wire
  // (`esignature?`, like `crm?`/`psa?`); the v2 schema omits this attribute,
  // so it is read off a widened attributes type.
  const { data: accountData, isLoading: accountLoading } =
    useV2GetAccountQuery({});
  const accountAttributes = accountData?.data?.attributes as
    | { "esignature?"?: boolean }
    | undefined;
  const esignatureEnabled = !!accountAttributes?.["esignature?"];

  const generateDocumentOptions = useMemo<GenerateDocumentOption[]>(() => {
    const options: GenerateDocumentOption[] = [
      { name: "Download", id: "download" },
      { name: "Email", id: "email" },
    ];
    if (saveToCloudStorageFlag) {
      options.push({ name: "Save To Cloud Storage", id: "cloud_storage" });
    }
    if (esignatureEnabled) {
      options.push({ name: "Electronic Signature", id: "docusign" });
    }
    return options;
  }, [saveToCloudStorageFlag, esignatureEnabled]);

  const { data: downloadTypesData, isLoading: downloadTypesLoading } =
    useV2ListProjectDownloadTypesForProjectQuery(
      { projectId },
      { skip: !project_id }
    );
  const documentTypes: V2ProjectDownloadTypeResource[] = useMemo(
    () => downloadTypesData?.data ?? [],
    [downloadTypesData]
  );

  const isLoading = accountLoading || downloadTypesLoading;

  // Default the document type selection once, from the project's document
  // template when it is one of the download types, else the first type.
  const documentTypeDefaulted = useRef(false);
  useEffect(() => {
    if (documentTypeDefaulted.current || documentTypes.length === 0) return;
    documentTypeDefaulted.current = true;

    const templateId = project?.relationships?.["document-template"]?.data?.id;
    const foundTemplate = templateId
      ? documentTypes.find((template) => template?.id == templateId)
      : undefined;
    const defaultType = toDocumentType(foundTemplate ?? documentTypes[0]);

    setSelectedDocumentType(defaultType);
    setSelectedDocumentTypes([defaultType]);
    setFilename(documentTypes[0].attributes?.filename ?? null);
  }, [documentTypes]);

  const { data: accountConnectionsData } = useV2ListAccountConnectionsQuery(
    {},
    { skip: !saveToCloudStorageFlag }
  );
  const { data: connectedAppsData } = useV2ListConnectedAppQuery(undefined, {
    skip: !saveToCloudStorageFlag,
  });

  // Pair each merge.dev connection with the matching connected-app provider,
  // then preselect the connection the project last delivered to (or the
  // first). Runs once, when both lists have arrived.
  const storageInitialized = useRef(false);
  useEffect(() => {
    if (storageInitialized.current) return;
    const accountConnections: any[] | undefined = accountConnectionsData?.data;
    const connectedApps: any[] | undefined = connectedAppsData?.data;
    if (!accountConnections || !connectedApps) return;
    storageInitialized.current = true;

    const mergeDevAccountConnections = accountConnections.filter(
      (ac) => ac?.attributes?.service === "merge_dev"
    );
    const mergeDevConnectedApp = connectedApps.find(
      (app) => app?.attributes?.service === "merge_dev"
    );
    const integrations: any[] = [];
    mergeDevAccountConnections.forEach((ac) => {
      (mergeDevConnectedApp?.attributes?.providers ?? []).forEach(
        (provider) => {
          if (
            provider?.name
              ?.toLowerCase()
              .includes(ac?.attributes?.settings?.storage)
          ) {
            integrations.push({ provider, connection: ac });
          }
        }
      );
    });
    setStorageIntegrations(integrations);

    let accountConnection;
    let integration;
    if (storageOptions && Object.keys(storageOptions).length !== 0) {
      accountConnection = accountConnections.find(
        (ac) => ac.id == Object.keys(storageOptions)[0]
      );
      integration = integrations.find((si) =>
        si.provider.name
          .toLowerCase()
          .includes(accountConnection?.attributes?.settings?.storage)
      );
    } else {
      accountConnection = integrations[0]?.connection;
      integration = integrations[0];
    }
    setSelectedConnection(accountConnection ?? null);
    setSelectedIntegration(integration ?? null);
  }, [accountConnectionsData, connectedAppsData]);

  // Poll the document request until it finishes or fails.
  const { data: documentRequestData } = useV2GetDocumentRequestQuery(
    { id: documentRequestId ?? "", include: ["project-documents"] },
    {
      skip: !documentRequestId || documentRequestSettled,
      pollingInterval: DOCUMENT_REQUEST_POLL_MS,
    }
  );

  useEffect(() => {
    const documentRequest = documentRequestData?.data;
    if (
      !documentRequest ||
      documentRequestSettled ||
      String(documentRequest.id) !== String(documentRequestId)
    ) {
      return;
    }
    const { status, "document-url": url } = documentRequest.attributes ?? {};

    if (status === "finished") {
      setDocumentRequestSettled(true);
      setDocumentURL(url ?? null);
      setGenerationStatus("success");
      dispatch(refreshCachedProject(projectId));
    } else if (status === "failed") {
      setDocumentRequestSettled(true);
      const included = documentRequestData?.included ?? [];
      const projectDocuments = (
        documentRequest.relationships?.["project-documents"]?.data ?? []
      ).map(
        (pj) =>
          included.find((i) => i.id == pj.id) as
            | V2ProjectDocumentResource
            | undefined
      );
      setErrorMessages(
        <ul>
          {projectDocuments.map((document, i) => {
            const atts = document?.attributes;
            const error = atts?.["error-text"] || "";
            const status = atts?.status;
            const name = atts?.["template-name"] || atts?.["document-type"];
            const url = atts?.["document-url"];
            const isFinished = status === "finished";
            return (
              <li
                style={{
                  textAlign: "start",
                  color: isFinished ? "#1c2655" : "#B00020",
                }}
                key={i}
              >
                <strong>{name}: </strong>
                <span>
                  {status} - {error}
                </span>
                {isFinished && url && (
                  <a
                    className="fileDownload"
                    href={url}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownload(url, name);
                    }}
                  >
                    Download
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      );
      setGenerationStatus("error");
      dispatch(refreshCachedProject(projectId));
    }
  }, [documentRequestData]);

  const [createDocumentRequest] = useV2CreateDocumentRequestMutation();

  const renderProjectDownLoadOptions = () => {
    let options = new Set();
    let documentGroups = new Set();
    documentTypes.forEach((documentType) => {
      documentGroups.add(documentType?.["attributes"]?.["group"]);
    });

    Array.from(documentGroups).forEach((docGroup) => {
      options.add({
        label: docGroup,
        options: documentTypes
          .filter((docType) => docType?.["attributes"]?.["group"] === docGroup)
          .map((dt) => {
            return {
              label: dt?.["attributes"]?.["name"],
              value: dt?.["attributes"]?.["template-id"],
              filename: dt?.attributes?.filename,
              type: dt?.attributes?.["document-type"],
            };
          }),
      });
    });
    return Array.from(options);
  };
  const resetForm = () => {
    setStep(1);
    setShowFileTreeModal(false);
    setEmailList("");
    setEmailListChanged(false);
    setForceRegeneration(false);
    setGeneratePdf(false);
  };
  const closeModal = () => {
    resetForm();
    setShow(false);
  };
  const resetGeneration = () => {
    setDocumentRequestId(null);
    setDocumentRequestSettled(false);
    setDocumentURL(null);
    setshowSuccessfulGenerationModal(false);
    setSelectedDeliveryMethod("download");
    setEmailFieldChecked(false);
    setSaveToCloudStorageChecked(false);
    setGenerationInFlight(false);
  };
  const closeSuccessModal = () => {
    resetGeneration();
    setShow(false);
  };
  const tryAgain = () => {
    // Back to the form: `show` is still true, so only the local hide lifts.
    resetGeneration();
    setShow(true);
  };

  const generateDocument = async (e) => {
    e.preventDefault();
    setGenerationStatus("loading");
    setshowSuccessfulGenerationModal(true);
    setDocumentRequestSettled(false);
    setGenerationInFlight(true);

    resetForm();

    const data: V2DocumentRequestResource = {
      type: "document-requests",
      attributes: {
        "force-regeneration": forceRegeneration,
        "generate-pdf": generatePdf,
        // `cloud_storage` is accepted by the API but missing from the v2 enum.
        "delivery-type":
          selectedDeliveryMethod as V2DocumentRequestResource["attributes"]["delivery-type"],
        "email-list":
          selectedDeliveryMethod === "email" ? emailList : undefined,
        filepath:
          selectedDeliveryMethod == "cloud_storage"
            ? selectedFolder?.name
            : undefined,
        templates: selectedDocumentTypes.map((dt) => {
          return {
            document_type: dt.type as TemplateDocumentType,
            template_id: String(dt.value),
            filename: dt.filename,
          };
        }),
      },
      relationships: {
        project: { data: { id: projectId, type: "projects" } },
      },
    };

    try {
      const response = await createDocumentRequest({ body: { data } }).unwrap();
      setDocumentRequestId(response?.data?.id ?? null);
    } catch (err) {
      const exception = (err as any)?.data?.errors?.[0]?.meta?.exception;
      const message = withErrorReason(
        "Failed to create document-request.",
        err
      );
      setErrorMessages(
        exception && !message.includes(exception)
          ? `${message} ${exception}`
          : message
      );
      setGenerationStatus("error");
    }
  };
  const getGenerateForm = () => {
    return step === 1 ? (
      <Form
        style={{
          display: "flex",
          flexDirection: orientation === "horizontal" ? "row" : "column",
        }}
        className="generateForm"
      >
        <div style={{ width: "400px" }}>
          <div className="text20">Generation Method</div>
          <p>What type of document would you like to generate?</p>
          <MultiSelectAutocomplete
            label="*Document Type"
            placeholder="Search and select multiple document types"
            className="documentTypes"
            options={
              renderProjectDownLoadOptions() as {
                value: string;
                label: string;
              }[]
            }
            value={selectedDocumentTypes}
            onChange={(e) => {
              //@ts-ignore
              setSelectedDocumentTypes(e || []);
            }}
            required={true}
          />
          {documentTypes?.find(
            (documentType) =>
              String(documentType?.attributes?.["template-id"]) ===
              String(selectedDocumentType?.value)
          )?.attributes?.["document-type"] === "sow" && (
            <Form.Check
              style={{ color: "#418172", marginTop: "10px" }}
              type="checkbox"
              id="generatePdfCheck"
              label="Convert Word Document to PDF?"
              checked={generatePdf}
              onChange={(e) => {
                setGeneratePdf(e.target.checked);
              }}
            />
          )}
          <FormFieldLabel
            className="marginTop10"
            label="How would you like to deliver this document?"
          />
          {generateDocumentOptions.map((item, index) => {
            if (
              documentTypes?.find(
                (documentType) =>
                  documentType?.attributes?.["template-id"] ==
                  String(selectedDocumentType?.value)
              )?.attributes?.["document-type"] !== "sow" &&
              item.name === "Electronic Signature"
            ) {
              return null;
            } else {
              return (
                <FormCheck
                  checked={item?.id === selectedDeliveryMethod ? true : false}
                  required={true}
                  id={item.id}
                  className="deliveryCheckbox"
                  key={index}
                  label={item.name}
                  name="group1"
                  type="radio"
                  onChange={(e) => {
                    setSelectedDeliveryMethod(e?.target?.id);
                    if (e.target.id == "email") {
                      setEmailFieldChecked(true);
                    } else {
                      setEmailFieldChecked(false);
                    }
                    if (e.target.id == "cloud_storage") {
                      setSaveToCloudStorageChecked(true);
                    } else {
                      setSaveToCloudStorageChecked(false);
                    }
                  }}
                />
              );
            }
          })}
          <Form.Check
            style={{
              color: "#418172",
              marginTop: "2em",
              minWidth: "165px",
              marginBottom: "15px",
            }}
            type="checkbox"
            id="forceRegenerationCheck"
            label="Force regeneration?"
            checked={forceRegeneration}
            onChange={(e) => {
              setForceRegeneration(e.target.checked);
            }}
          />
        </div>
        {emailFieldChecked && (
          <div
            className={
              orientation === "horizontal"
                ? "emailSectionHorizontal"
                : "emailSectionVertical"
            }
          >
            <div className="text20">Send by Email</div>
            <p>Where would you like the generated document sent?</p>
            <Form.Group className="formGroup">
              <FormFieldLabel
                className={""}
                label="*Email List (comma separated list of emails)"
              />
              <Form.Control
                required={true}
                value={emailList}
                onChange={(e) => {
                  setEmailListChanged(true);
                  setEmailList(e.target.value);
                }}
                isInvalid={emailsAreInvalid && emailListChanged}
                onBlur={() =>
                  setEmailsAreInvalid(
                    (!validateEmails(emailList) || emailList === "") &&
                      selectedDeliveryMethod === "email"
                  )
                }
              />
              {emailsAreInvalid && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid list of emails
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>
        )}
        {saveToCloudStorageChecked && (
          <div
            className={
              orientation === "horizontal"
                ? "emailSectionHorizontal"
                : "emailSectionVertical"
            }
          >
            <div className="text20">Save To Cloud Storage</div>
            <p>Choose your cloud storage provider.</p>
            <div className="flexCenter" style={{ marginTop: "-20px" }}>
              {storageIntegrations.map((si) => {
                return (
                  <img
                    onClick={() => {
                      setSelectedConnection(si?.connection);
                      setFolderLoading(true);
                    }}
                    key={si?.provider?.name}
                    style={
                      selectedConnection?.["id"] == si?.connection?.id
                        ? {
                            border: "2px solid #418172",
                            width: "110px",
                            cursor: "pointer",
                            borderRadius: "7%",
                            margin: "5px",
                            padding: "5px",
                          }
                        : { width: "110px", cursor: "pointer", padding: "5px" }
                    }
                    src={si?.provider?.logo}
                  />
                );
              })}
            </div>
            {!folderLoading ? (
              <Form.Group className="formGroup">
                <FormFieldLabel
                  className={""}
                  label={
                    selectedConnection
                      ? `*${snakeToText(
                          selectedConnection?.["attributes"]?.["settings"]?.[
                            "storage"
                          ]
                        )} Storage Folder`
                      : "*Storage Folder"
                  }
                />
                {selectedFolder ? (
                  <Form.Control
                    type="text"
                    readOnly={true}
                    value={selectedFolder?.name}
                  />
                ) : (
                  <SmallSpinner />
                )}
                <small
                  onClick={() => setShowFileTreeModal(true)}
                  className="editRoot"
                >
                  Edit Storage Folder
                </small>
              </Form.Group>
            ) : (
              <SmallSpinner />
            )}
          </div>
        )}
        {isStationary && (
          <Button
            style={
              selectedDeliveryMethod !== "email" ? { marginTop: "20px" } : {}
            }
            onClick={(e) => (step === 1 ? setStep(2) : generateDocument(e))}
            className="seafoamBgButton"
            disabled={
              ((!validateEmails(emailList) || emailList === "") &&
                emailFieldChecked) ||
              (saveToCloudStorageChecked &&
                (selectedFolder.id === "" || !filename)) ||
              selectedDocumentTypes.length === 0
            }
          >
            Generate
          </Button>
        )}
      </Form>
    ) : (
      <div className="docGenFileNaming">
        <div className="text20">File Naming </div>
        <p style={{ marginTop: "5px" }}>
          View / edit filenames for each document type selected.
        </p>
        <div className="filenames">
          {selectedDocumentTypes.map((dt) => {
            return (
              <div key={dt.value} className="docType">
                <div
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    color: "#418172",
                    marginBottom: "5px",
                  }}
                >
                  <div style={{ fontWeight: "700", marginRight: "5px" }}>
                    *Filename
                  </div>
                  <div>({dt.label})</div>
                </div>
                <Form.Control
                  required={true}
                  value={dt.filename}
                  onChange={(e) => {
                    const updated = selectedDocumentTypes.map((sdt) =>
                      sdt.value === dt.value
                        ? { ...sdt, filename: e.target.value }
                        : sdt
                    );
                    setSelectedDocumentTypes(updated);
                  }}
                  isInvalid={false}
                />
              </div>
            );
          })}
        </div>
        {isStationary && (
          <div
            style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={(e) => setStep(1)}
              variant="secondary"
              className="btnSeawhite"
              style={{ width: "45%" }}
            >
              Back
            </Button>
            <Button
              onClick={(e) => generateDocument(e)}
              className="seafoamBgButton"
              disabled={
                selectedDocumentTypes.find((dt) => !dt.filename) ? true : false
              }
              style={{ width: "45%" }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        !!downloadError,
        downloadError,
        () => setDownloadError(""),
        "danger",
        ToastAlert
      )}
      {isStationary ? (
        <StationaryModal
          header={"Generate Document"}
          body={isLoading ? <SmallSpinner /> : getGenerateForm()}
        />
      ) : (
        <ScopeStackModal
          className={
            orientation === "horizontal" &&
            (emailFieldChecked || saveToCloudStorageChecked)
              ? "wide "
              : ""
          }
          modalTitle={"Generate Document"}
          modalBody={isLoading ? <SmallSpinner /> : getGenerateForm()}
          button1Text={step === 1 ? "Cancel" : "Back"}
          handleButton1Click={() => (step === 1 ? closeModal() : setStep(1))}
          button2Text={step === 1 ? "Generate" : "Save"}
          handleButton2Click={(e) =>
            step === 1 ? setStep(2) : generateDocument(e)
          }
          button2Disabled={
            ((!validateEmails(emailList) || emailList === "") &&
              emailFieldChecked) ||
            (saveToCloudStorageChecked &&
              (selectedFolder?.id === "" || !filename)) ||
            selectedDocumentTypes.length === 0
          }
          show={show && !generationInFlight}
          handleClose={() => closeModal()}
        />
      )}
      <Modal
        centered
        show={showSuccessfulGenerationModal}
        onHide={closeSuccessModal}
      >
        <Modal.Body className="genDocModal">
          {(() => {
            switch (generationStatus) {
              case "success":
                return (
                  <div>
                    <div className="headerWithX">
                      <p className="genDocTitle">Generate Document</p>
                      <div
                        className="XIcon"
                        onClick={closeSuccessModal}
                        style={{ cursor: "pointer" }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                        >
                          <path
                            d="M19 6.91L17.59 5.5L12 11.09L6.41 5.5L5 6.91L10.59 12.5L5 18.09L6.41 19.5L12 13.91L17.59 19.5L19 18.09L13.41 12.5L19 6.91Z"
                            fill="#1C2655"
                          />
                        </svg>
                      </div>
                    </div>
                    <hr />
                    <div className="modalImageWithText">
                      {/* The div below(modalDisplayImages) is the graphic for the success modal */}
                      <div className="modalDisplayImages">
                        <img src={successVector} style={{ display: "block" }} />
                        <img
                          src={successCircle}
                          style={{
                            position: "absolute",
                            top: "0",
                            left: "60%",
                            display: "block",
                            transform: "translate(5%, 180%)",
                          }}
                        />
                      </div>
                      <p className="genDocWordBody">
                        {selectedDeliveryMethod === "download"
                          ? "Document generated successfully!"
                          : "Document generated and sent successfully!"}
                      </p>
                    </div>
                  </div>
                );
              case "error":
                return (
                  <div>
                    <div className="headerWithX">
                      <p className="genDocTitle">Generate Document</p>
                      <div
                        className="XIcon"
                        onClick={closeSuccessModal}
                        style={{ cursor: "pointer" }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                        >
                          <path
                            d="M19 6.91L17.59 5.5L12 11.09L6.41 5.5L5 6.91L10.59 12.5L5 18.09L6.41 19.5L12 13.91L17.59 19.5L19 18.09L13.41 12.5L19 6.91Z"
                            fill="#1C2655"
                          />
                        </svg>
                      </div>
                    </div>
                    <hr />
                    {/* The div below(modalDisplayImages) is the graphic for the error modal */}
                    <div className="modalDisplayImages">
                      <img src={errorVector} style={{ display: "block" }} />
                      <img
                        src={errorCircle}
                        style={{
                          position: "absolute",
                          top: "0",
                          left: "60%",
                          display: "block",
                          transform: "translate(5%, 180%)",
                        }}
                      />
                    </div>
                    <p className="genDocWordBody">
                      There was a problem generating your document. If this
                      error persists, please contact support.
                    </p>
                    <div
                      style={{
                        color: "#B00020",
                        textAlign: "center",
                        padding: "0 16px",
                        whiteSpace: "pre-line",
                        marginTop: "8px",
                      }}
                    >
                      {errorMessages}
                    </div>
                    <hr />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "right",
                        margin: "0px 16px 16px 16px",
                        gap: "8px",
                      }}
                    >
                      <Button
                        className="modalCancelButton"
                        onClick={closeSuccessModal}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="modalDownloadButton"
                        onClick={tryAgain}
                      >
                        Try Again
                      </Button>
                    </div>
                  </div>
                );
              case "loading":
              default:
                return (
                  <div>
                    <div className="headerWithX">
                      <p className="genDocTitle">Generate Document</p>
                      <div
                        className="XIcon"
                        onClick={closeSuccessModal}
                        style={{ cursor: "pointer" }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                        >
                          <path
                            d="M19 6.91L17.59 5.5L12 11.09L6.41 5.5L5 6.91L10.59 12.5L5 18.09L6.41 19.5L12 13.91L17.59 19.5L19 18.09L13.41 12.5L19 6.91Z"
                            fill="#1C2655"
                          />
                        </svg>
                      </div>
                    </div>
                    <hr />
                    <div
                      className="GenDocModalBody"
                      style={{ margin: "54px 0px 46px 0px" }}
                    >
                      {/* The div below(modalDisplayImages) is the graphic for the loading modal */}
                      <div className="modalDisplayImages">
                        <img src={loadingGif} style={{ marginTop: "-50px" }} />
                      </div>
                      <p className="genDocWordBody">
                        The document is being generated and will be{" "}
                        {selectedDeliveryMethod === "download"
                          ? "available to download"
                          : "sent"}{" "}
                        as soon as it is finished.
                      </p>
                    </div>
                    <hr />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "right",
                        margin: "0px 16px 16px 16px",
                      }}
                    >
                      <Button className="modalLoadingButton">Generating</Button>
                    </div>
                  </div>
                );
            }
          })()}
        </Modal.Body>
        {documentURL && (
          <Modal.Footer>
            <>
              <Button className="modalCancelButton" onClick={closeSuccessModal}>
                Cancel
              </Button>
              <Button
                className="modalDownloadButton"
                onClick={() =>
                  handleDownload(documentURL, filename ?? undefined)
                }
              >
                Download
              </Button>
            </>
          </Modal.Footer>
        )}
      </Modal>
      <FolderTreeComponentModal
        showFileTreeModal={showFileTreeModal}
        setShowFileTreeModal={setShowFileTreeModal}
        showCheckbox={false}
        readOnly={false}
        indentPixels={40}
        authorizationCode={authorizationCode}
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
        folderLoading={folderLoading}
        setFolderLoading={setFolderLoading}
        selectedConnection={selectedConnection}
        setSelectedConnection={setSelectedConnection}
        selectedIntegration={selectedIntegration}
        setSelectedIntegration={setSelectedIntegration}
        storageOptions={storageOptions}
        canCreateFolder={false}
        canEditFolderName={false}
        isChooseRootFolder={false}
      />
    </div>
  );
};

export default GenerateDocument;
